import { useState, useEffect, useRef } from 'react';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import { withBreakpointsContext } from 'Shared/utils/breakpoints';
import { css } from '@emotion/css';
import { space, borderRadius, color, shadow } from '@ifixit/primitives';
import styled from 'styled-components';
import { WikiSearch, HitType } from 'Guide/wiki_search';
import { CategoryCard } from 'Guide/category_card';
import {
   setSelectedWikiDevice,
   setSavingState,
   getSavingState,
   onSavingStateChange,
} from 'Shared/ifixit_store';
import Validation from 'Shared/validation';

export function NewGuideWikiSearch({
   namespace,
   placeHolder,
   tabIndex,
}: {
   namespace: string;
   placeHolder: string;
   tabIndex: number;
}) {
   // useRef to get up-to-date values in handleClickOutside
   const focusedRef = useRef<boolean>(false);
   const [valid, setValid] = useState(true);
   const [saving, setSaving] = useState(false);
   const [catCard, setCatCard] = useState(false);
   const [clickedOnForm, setClickedOnForm] = useState(false);
   const [userQuery, setUserQuery] = useState('');
   const isInvalidTitle = (!valid && clickedOnForm) || (!valid && saving) || (!userQuery && saving);

   // We are using redux values so we can validate
   // our react component values in guide_intro before we
   // use form manager to validate the non-react components
   useEffect(() => {
      onSavingStateChange(() => setSaving(getSavingState()));
   }, [getSavingState()]);

   const ref = useRef<HTMLDivElement>(null);
   const handleClickOutside = (e: any) => {
      // Only close the dropdown if the WikiSearch is focused and the click is outside the WikiSearch
      if (focusedRef.current && ref.current && !ref.current.contains(e.target)) {
         const inputs = ref.current.querySelectorAll('input');
         if (inputs && inputs.length > 0) {
            // eslint-disable-next-line react-hooks/immutability
            closeDropdown(inputs[0].value);
         }
      }
   };
   const handleTab = (e: any) => {
      if (e.key == 'Tab') {
         closeDropdown(e.target.value);
      }
   };
   useEffect(() => {
      document.addEventListener('click', handleClickOutside, true);
      return () => {
         document.removeEventListener('click', handleClickOutside, true);
      };
   }, []);

   useEffect(() => {
      const currentRef = ref.current;
      if (currentRef) {
         currentRef.addEventListener('keydown', handleTab, false);
         return () => {
            currentRef.removeEventListener('keydown', handleTab, false);
         };
      }
   }, []);

   function closeDropdown(query: string) {
      focusedRef.current = false;
      const title = query ? query : '';
      setValid(Validation.isValidWikiTitle(title));
      setUserQuery(title);
      if (!catCard && title) {
         handleClickNewDevice({ query: title });
      }
      if (!title) {
         setClickedOnForm(true);
      }
   }

   function handleClickKnownDevice({ selectedItem }: { selectedItem: HitType }): void {
      const { device: title, image_url: image, description, title: displayTitle } = selectedItem;
      const wikiSearch = document.querySelector('[data-name="NewGuideWikiSearch"]');
      setSelectedWikiDevice(title);

      // @ts-expect-error TS(2345) FIXME: Argument of type 'Element | null' is not assignabl... Remove this comment to see the full error message
      const catCard = new CategoryCard(wikiSearch);

      catCard.setDeviceInfo(title, image, description, displayTitle);
      catCard.toggleDeviceBlurb(true);
      setCatCard(true);
      focusedRef.current = false;
   }

   function handleClickNewDevice({ query }: { query: string }): void {
      if (!query) {
         return;
      }
      const wikiSearch = document.querySelector('[data-name="NewGuideWikiSearch"]');
      setSelectedWikiDevice(query);

      // @ts-expect-error TS(2345) FIXME: Argument of type 'Element | null' is not assignabl... Remove this comment to see the full error message
      const catCard = new CategoryCard(wikiSearch);

      catCard.setDeviceInfo(query);
      catCard.toggleDeviceBlurb(true);
      setCatCard(true);
   }

   function handleFocus(): void {
      focusedRef.current = true;
      setSavingState(false);
      setCatCard(false);
   }

   return (
      <div className="topicChooser" ref={ref} style={{ position: 'relative' }}>
         <WikiSearch
            namespace={namespace}
            placeHolder={placeHolder}
            buttonType={'+'}
            formTabIndex={tabIndex}
            buttonTabIndex={tabIndex}
            styledSearchBarContainer={SearchBarContainer}
            styledSearchResultsContainer={SearchResultsContainer}
            inputClassName={PageTypeTitleInput + ' ' + 'watchModified topic'}
            isInvalidTitle={isInvalidTitle}
            // @ts-expect-error TS(2322) FIXME: Type '({ query }: { query: string; }) => void' is ... Remove this comment to see the full error message
            onNewDeviceSelected={handleClickNewDevice}
            onKnownDeviceSelected={handleClickKnownDevice}
            onFocus={handleFocus}
         />
      </div>
   );
}

const PageTypeTitleInput = css`
   flex: 1 0 auto;
   width: 100%;
   height: 100%;
   padding: ${space[2]};
   margin: ${space[0]};
   font-size: ${space[4]};
   border: 1px solid #c1c1c1; //@borderDark
   border-radius: ${borderRadius['md']} 0 0 ${borderRadius['md']};
   outline: none;
   &:focus {
      border: 2px solid #0066cc;
      box-shadow: none;
   }
`;

const SearchBarContainer = styled.div`
   display: flex;
   height: ${space[6]};
   //matches variables.less > @mobile
   @media only screen and (max-width: 599px) {
      width: 100%;
   }
`;

const SearchResultsContainer = styled.div`
   border: ${color.gray[400]} 1px solid;
   box-shadow: ${shadow[3]};
   position: absolute;
   width: 100%;
   z-index: 1;
   &:empty {
      border: none;
   }
`;

initializeChakraComponent('NewGuideWikiSearch', withBreakpointsContext(NewGuideWikiSearch));
